/**
 * tutorTrainingAdapter — la formation du tuteur (`tutorTraining`, `GET tutor-training-steps`) → ce que
 * « Ta formation » affiche, et ses réponses → le corps de `submit-quizz`.
 *
 * Port littéral de `tutor/training/TutorTrainingScreen.js` : le seuil (`Math.ceil(total × seuil %)`),
 * la formation validée (`progress.status === "passed"`), le corps (`step_answers: { id: [réponse] }`)
 * et la correction (`lastResult.results`, une ligne par question dans l'ordre du quiz, chacune avec la
 * réponse choisie et la bonne).
 */
import { quizOptionMark } from "../../../ui/formatters/quizReviewFormatter"
import { type UIQuizReviewQuestion } from "../../../ui/types/studentQuizReview"
import { type UITutorTrainingQuestion, type UITutorTrainingVideo } from "../../../ui/types/tutorCompte"

export type ApiTrainingStep = {
    id?: number | null
    title?: string | null
    description?: string | null
    embed_url?: string | null
    video_url?: string | null
}

export type ApiTrainingQuestion = {
    id?: number | null
    question_string?: string | null
    answers?: string[] | null
}

export type ApiTrainingProgress = { status?: string | null } | null

export type ApiTrainingResultRow = {
    question_id?: number | null
    correct?: boolean | null
    valid_answers?: string[] | null
    tutor_answers?: string[] | null
}

export type ApiTrainingResult = {
    passed?: boolean | null
    correct_count?: number | null
    total?: number | null
    results?: ApiTrainingResultRow[] | null
} | null

/** Le réducteur `tutorTraining`, tel que le serveur le remplit. */
export type ApiTutorTraining = {
    step?: ApiTrainingStep | null
    questions?: ApiTrainingQuestion[] | null
    passThresholdPercent?: number | null
    progress?: ApiTrainingProgress
    lastResult?: ApiTrainingResult
}

/** Le résultat d'un envoi, prêt pour l'étape « result » de la page. */
export type UITutorTrainingResult = {
    passed: boolean
    correctCount: number
    percentage: number
    review: UIQuizReviewQuestion[]
}

/** Le seuil de l'ancien écran, quand le serveur ne le donne pas. */
const DEFAULT_THRESHOLD_PERCENT = 80

export function toTrainingVideo(step: ApiTrainingStep | null | undefined): UITutorTrainingVideo | null {
    if (step == null) return null
    return {
        title: step.title ?? "",
        description: step.description ?? "",
        embedUrl: step.embed_url || null,
    }
}

export function toTrainingQuestions(questions: ApiTrainingQuestion[] | null | undefined): UITutorTrainingQuestion[] {
    return (questions ?? [])
        .map((question): UITutorTrainingQuestion | null =>
            question?.id == null
                ? null
                : { id: question.id, text: question.question_string ?? "", options: question.answers ?? [] }
        )
        .filter((question): question is UITutorTrainingQuestion => question != null)
}

/** Les bonnes réponses qu'il faut : 16 sur 20 au seuil de 80 %. */
export function trainingNeededCount(total: number, thresholdPercent: number | null | undefined): number {
    return Math.ceil((total * (thresholdPercent ?? DEFAULT_THRESHOLD_PERCENT)) / 100)
}

export function isTrainingPassed(progress: ApiTrainingProgress | undefined): boolean {
    return progress?.status === "passed"
}

/** Le corps de `submitTutorTrainingQuiz` : une seule réponse par question, dans une liste. */
export function toQuizAnswersPayload(answers: Record<number, string>) {
    return {
        step_answers: Object.fromEntries(Object.entries(answers).map(([id, answer]) => [id, [answer]])),
    }
}

/**
 * La correction : une ligne par question corrigée, dans l'ordre du serveur (celui du quiz), son énoncé
 * et ses réponses repris des questions lues. La page range elle-même les manquées d'abord.
 */
export function toTrainingResult(
    result: ApiTrainingResult | undefined,
    questions: ApiTrainingQuestion[] | null | undefined
): UITutorTrainingResult | null {
    if (result == null) return null
    const byId = new Map((questions ?? []).map((question) => [question?.id, question]))
    const review = (result.results ?? [])
        .map((row): UIQuizReviewQuestion | null => {
            if (row?.question_id == null) return null
            const question = byId.get(row.question_id)
            const chosen = row.tutor_answers ?? []
            const outcome = row.correct ? "right" : "wrong"
            const mark = {
                answer: chosen[0] ?? null,
                correctAnswer: row.valid_answers?.[0] ?? null,
                result: outcome,
            } as const
            return {
                id: row.question_id,
                text: question?.question_string ?? "",
                options: (question?.answers ?? []).map((label) => ({ label, mark: quizOptionMark(label, mark) })),
                answer: chosen.length > 0 ? chosen.join(", ") : null,
                result: outcome,
            }
        })
        .filter((question): question is UIQuizReviewQuestion => question != null)
    const total = result.total ?? review.length
    const correctCount = result.correct_count ?? 0
    return {
        passed: result.passed === true,
        correctCount,
        percentage: total > 0 ? Math.round((correctCount * 100) / total) : 0,
        review,
    }
}
